import { UserManager, WebStorageStateStore, User } from "oidc-client-ts";
import { cognitoAuthority, cognitoHostedUiBase, config } from "./config";

// Cognito's logout endpoint is non-standard (not in the OIDC discovery doc),
// so we handle sign-out manually by redirecting to /logout with the right
// query params, after clearing the local oidc-client-ts user.
export const userManager = new UserManager({
  authority: cognitoAuthority(),
  client_id: config.cognito.clientId,
  redirect_uri: `${config.app.url}/auth/callback`,
  response_type: "code",
  scope: "openid email profile",
  loadUserInfo: false,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Don't auto-refresh in the background: keep it simple and let the user
  // re-auth when the token expires. Can be enabled later with silent renew.
  automaticSilentRenew: false,
});

export async function getUser(): Promise<User | null> {
  const user = await userManager.getUser();
  if (!user || user.expired) return null;
  return user;
}

export async function login(): Promise<void> {
  await userManager.signinRedirect();
}

export async function handleCallback(): Promise<User> {
  return userManager.signinRedirectCallback();
}

export async function logout(): Promise<void> {
  await userManager.removeUser();
  // Cognito-specific logout endpoint clears the Hosted UI session
  const logoutUrl =
    `${cognitoHostedUiBase()}/logout` +
    `?client_id=${encodeURIComponent(config.cognito.clientId)}` +
    `&logout_uri=${encodeURIComponent(config.app.url + "/")}`;
  window.location.assign(logoutUrl);
}

export async function getAccessToken(): Promise<string | null> {
  const user = await getUser();
  return user?.access_token ?? null;
}

// Group names must match the Cognito groups created in `infra/cdk/stacks/auth_stack.py`.
// Kept in sync via CLAUDE.md; group names are stable across envs (no -dev/-prod suffix).
export const ADMIN_GROUP = "cockpit-admin";
export const USER_GROUP = "cockpit-users";

function readGroups(user: User | null): string[] {
  if (!user) return [];
  // oidc-client-ts puts ID-token claims on `profile`. Cognito emits
  // `cognito:groups` as a JSON array of strings.
  const raw = (user.profile as Record<string, unknown> | undefined)?.["cognito:groups"];
  if (Array.isArray(raw)) return raw.map((g) => String(g));
  if (typeof raw === "string") return [raw];
  return [];
}

export type Membership = {
  email: string | null;
  groups: string[];
  isMember: boolean;
  isAdmin: boolean;
};

export async function getMembership(): Promise<Membership | null> {
  const user = await getUser();
  if (!user) return null;
  const groups = readGroups(user);
  const isAdmin = groups.includes(ADMIN_GROUP);
  // Admins are implicitly members — mirror the API's _is_member() logic.
  const isMember = isAdmin || groups.includes(USER_GROUP);
  const email =
    (user.profile as { email?: string } | undefined)?.email ?? null;
  return { email, groups, isMember, isAdmin };
}
